import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import api, { clearAuthAndRedirect, fetchSessionConfig } from '../../services/api';
import sessionManager from '../../services/sessionManager';

// Get stored auth data
const storedToken = localStorage.getItem('accessToken');
const storedUser = localStorage.getItem('user');

const initialState = {
  user: storedUser ? JSON.parse(storedUser) : null,
  accessToken: storedToken || null,
  refreshToken: localStorage.getItem('refreshToken') || null,
  isAuthenticated: !!storedToken,
  loading: false,
  error: null,
  sessionExpiredMessage: null,
};

// Async thunks
/**
 * loginWithTokens — called by MicrosoftCallback after storing tokens in localStorage.
 * Reads localStorage state and bootstraps the Redux auth slice.
 */
export const loginWithTokens = createAsyncThunk(
  'auth/loginWithTokens',
  async (_, { rejectWithValue }) => {
    try {
      const accessToken = localStorage.getItem('accessToken');
      const user = localStorage.getItem('user');
      if (!accessToken || !user) {
        return rejectWithValue('No credentials in storage');
      }
      // Fetch fresh user data from backend to pick up role changes
      try {
        const response = await api.get('/auth/me');
        const freshUser = response.data;
        localStorage.setItem('user', JSON.stringify(freshUser));
        return {
          access_token: accessToken,
          refresh_token: localStorage.getItem('refreshToken'),
          user: freshUser,
        };
      } catch {
        // If /me fails (e.g. token expired), fall back to cached user
        return {
          access_token: accessToken,
          refresh_token: localStorage.getItem('refreshToken'),
          user: JSON.parse(user),
        };
      }
    } catch (error) {
      return rejectWithValue('Failed to restore session');
    }
  }
);

// Kept for backward compatibility — Microsoft login is initiated via
// the Login.js component directly (browser redirect, not an API call).
export const login = loginWithTokens;

export const logout = createAsyncThunk(
  'auth/logout',
  async (_, { getState, rejectWithValue }) => {
    try {
      const { refreshToken } = getState().auth;
      // Send refresh token for server-side revocation
      await api.post('/auth/logout', { refresh_token: refreshToken });
    } catch (error) {
      // Continue with logout even if API call fails
    }

    // Destroy session manager
    sessionManager.destroy();

    // Clear storage
    localStorage.removeItem('accessToken');
    localStorage.removeItem('refreshToken');
    localStorage.removeItem('user');
    localStorage.removeItem('sessionConfig');
    localStorage.removeItem('lastActivity');

    return null;
  }
);

export const logoutAllSessions = createAsyncThunk(
  'auth/logoutAll',
  async (_, { rejectWithValue }) => {
    try {
      await api.post('/auth/logout-all');
    } catch (error) {
      // Continue with logout even if API call fails
    }

    // Destroy session manager
    sessionManager.destroy();

    // Clear storage
    localStorage.removeItem('accessToken');
    localStorage.removeItem('refreshToken');
    localStorage.removeItem('user');
    localStorage.removeItem('sessionConfig');
    localStorage.removeItem('lastActivity');

    return null;
  }
);

export const refreshAccessToken = createAsyncThunk(
  'auth/refresh',
  async (_, { getState, rejectWithValue }) => {
    try {
      const { refreshToken } = getState().auth;

      const response = await api.post('/auth/refresh', {
        refresh_token: refreshToken,
      });

      // Store new tokens (refresh token is rotated)
      localStorage.setItem('accessToken', response.data.access_token);
      if (response.data.refresh_token) {
        localStorage.setItem('refreshToken', response.data.refresh_token);
      }

      return response.data;
    } catch (error) {
      // Clear storage on refresh failure
      sessionManager.destroy();
      localStorage.removeItem('accessToken');
      localStorage.removeItem('refreshToken');
      localStorage.removeItem('user');
      localStorage.removeItem('sessionConfig');
      localStorage.removeItem('lastActivity');

      return rejectWithValue('Session expired');
    }
  }
);

export const initializeSession = createAsyncThunk(
  'auth/initializeSession',
  async (_, { rejectWithValue }) => {
    try {
      // Check if already authenticated
      const token = localStorage.getItem('accessToken');
      if (!token) {
        return null;
      }

      // Fetch session config and initialize session manager
      const sessionConfig = await fetchSessionConfig();
      sessionManager.init({
        idleTimeoutMinutes: sessionConfig.session_idle_timeout_minutes,
      });

      return sessionConfig;
    } catch (error) {
      return rejectWithValue('Failed to initialize session');
    }
  }
);

export const fetchCurrentUser = createAsyncThunk(
  'auth/fetchCurrentUser',
  async (_, { rejectWithValue }) => {
    try {
      const response = await api.get('/auth/me');
      localStorage.setItem('user', JSON.stringify(response.data));
      return response.data;
    } catch (error) {
      return rejectWithValue(
        error.response?.data?.detail || 'Failed to fetch user'
      );
    }
  }
);

const authSlice = createSlice({
  name: 'auth',
  initialState,
  reducers: {
    clearError: (state) => {
      state.error = null;
    },
    updateUser: (state, action) => {
      state.user = action.payload;
      localStorage.setItem('user', JSON.stringify(action.payload));
    },
    setSessionExpiredMessage: (state, action) => {
      state.sessionExpiredMessage = action.payload;
    },
    clearSessionExpiredMessage: (state) => {
      state.sessionExpiredMessage = null;
    },
    forceLogout: (state, action) => {
      // Called when session expires due to inactivity or other reasons
      state.user = null;
      state.accessToken = null;
      state.refreshToken = null;
      state.isAuthenticated = false;
      state.sessionExpiredMessage = action.payload || 'Your session has expired.';

      // Clean up storage
      sessionManager.destroy();
      localStorage.removeItem('accessToken');
      localStorage.removeItem('refreshToken');
      localStorage.removeItem('user');
      localStorage.removeItem('sessionConfig');
      localStorage.removeItem('lastActivity');
    },
  },
  extraReducers: (builder) => {
    builder
      // Microsoft SSO token bootstrap (called from MicrosoftCallback)
      .addCase(loginWithTokens.pending, (state) => {
        state.loading = true;
        state.error = null;
        state.sessionExpiredMessage = null;
      })
      .addCase(loginWithTokens.fulfilled, (state, action) => {
        state.loading = false;
        state.isAuthenticated = true;
        state.user = action.payload.user;
        state.accessToken = action.payload.access_token;
        state.refreshToken = action.payload.refresh_token;
        state.sessionExpiredMessage = null;
      })
      .addCase(loginWithTokens.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      // Logout
      .addCase(logout.fulfilled, (state) => {
        state.user = null;
        state.accessToken = null;
        state.refreshToken = null;
        state.isAuthenticated = false;
        state.sessionExpiredMessage = null;
      })
      // Logout all sessions
      .addCase(logoutAllSessions.fulfilled, (state) => {
        state.user = null;
        state.accessToken = null;
        state.refreshToken = null;
        state.isAuthenticated = false;
        state.sessionExpiredMessage = null;
      })
      // Refresh token
      .addCase(refreshAccessToken.fulfilled, (state, action) => {
        state.accessToken = action.payload.access_token;
        if (action.payload.refresh_token) {
          state.refreshToken = action.payload.refresh_token;
        }
      })
      .addCase(refreshAccessToken.rejected, (state) => {
        // Only show session expired message if user was previously authenticated
        const wasAuthenticated = state.isAuthenticated;
        state.user = null;
        state.accessToken = null;
        state.refreshToken = null;
        state.isAuthenticated = false;
        // Only set message if user had an active session
        state.sessionExpiredMessage = wasAuthenticated ? 'Your session has expired. Please login again.' : null;
      })
      // Fetch current user
      .addCase(fetchCurrentUser.fulfilled, (state, action) => {
        state.user = action.payload;
      })
      // Initialize session
      .addCase(initializeSession.fulfilled, (state) => {
        // Session initialized successfully
      });
  },
});

export const {
  clearError,
  updateUser,
  setSessionExpiredMessage,
  clearSessionExpiredMessage,
  forceLogout
} = authSlice.actions;
export default authSlice.reducer;
